// ============================================================================
// EPICSELECTOR.JS - Selector de épicas con opción de crear nuevas (CON PORTAL)
// ============================================================================

import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Target, Plus, X, Check, ChevronDown } from 'lucide-react';
import { useTaskStore } from '../store/TaskStore';
import { shallow } from 'zustand/shallow';
import { useAuth } from '../auth/AuthContext';
import useToast from '../services/useToast';
import { useIsPhone } from './shared/useMediaQuery';
import '../styles/epic-selector.css';

function EpicSelector({
  value,
  onChange,
  error,
  required = false,
  disabled = false,
  placeholder = "Seleccionar épica..."
}) {
  const { epics, createEpic, fetchEpics } = useTaskStore(
    (s) => ({ epics: s.epics, createEpic: s.createEpic, fetchEpics: s.fetchEpics }),
    shallow
  );
  const { token } = useAuth();
  const { showSuccess, showError } = useToast();

  // Estados locales
  const [isOpen, setIsOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newEpicName, setNewEpicName] = useState('');
  const [newEpicColor, setNewEpicColor] = useState('#6366f1');
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  // En teléfono, con más de cinco épicas, la lista se abre como hoja inferior
  const isPhone = useIsPhone();
  const activeEpicCount = epics.filter(epic => epic.activo).length;
  const asSheet = isPhone && (activeEpicCount > 5 || isCreating);

  // Referencias
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Cargar épicas al montar
  useEffect(() => {
    if (epics.length === 0) {
      fetchEpics(token);
    }
  }, [epics.length, fetchEpics, token]);

  // Calcular posición del dropdown cuando se abre
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      // Sujeto al viewport: ancho entre 280 y 500 px sin pasar del ancho
      // disponible, y left sin salirse por ningún lado (8 px de margen).
      const vw = window.innerWidth;
      const width = Math.min(Math.max(rect.width, 280), 500, vw - 16);
      const left = Math.max(8, Math.min(rect.left, vw - width - 8));
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 4,
        left: left + window.scrollX,
        width
      });
    }
  }, [isOpen]);

  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsOpen(false);
        setIsCreating(false);
        setSearchTerm('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Manejar teclas
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
        setIsCreating(false);
        setSearchTerm('');
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  // Hoja inferior (teléfono): foco inicial en el diálogo, sin abrir el teclado
  useEffect(() => {
    if (isOpen && asSheet && !isCreating) dropdownRef.current?.focus();
  }, [isOpen, asSheet, isCreating]);

  // Filtrar épicas activas por búsqueda
  const filteredEpics = epics
    .filter(epic => epic.activo)
    .filter(epic =>
      epic.nombre.toLowerCase().includes(searchTerm.toLowerCase())
    );

  // Obtener épica seleccionada
  const selectedEpic = epics.find(epic => epic.nombre === value);

  // Manejar selección
  const handleSelect = (epicName) => {
    onChange(epicName);
    setIsOpen(false);
    setSearchTerm('');
  };

  // Manejar creación de nueva épica
  const handleCreateEpic = async () => {
    if (!newEpicName.trim()) {
      showError('El nombre de la épica es obligatorio');
      return;
    }

    setIsLoading(true);

    try {
      const result = await createEpic({
        nombre: newEpicName.trim(),
        color: newEpicColor
      }, token);

      if (result.success) {
        showSuccess(`Épica "${result.data.nombre}" creada correctamente`);
        onChange(result.data.nombre);
        setIsCreating(false);
        setNewEpicName('');
        setNewEpicColor('#6366f1');
        setIsOpen(false);
      } else {
        showError(result.error || 'Error al crear épica');
      }
    } catch (error) {
      showError('Error al crear épica: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Colores predefinidos para nuevas épicas
  const colorOptions = [
    '#6366f1', // indigo
    '#3b82f6', // blue
    '#10b981', // green
    '#f59e0b', // amber
    '#ef4444', // red
    '#8b5cf6', // purple
    '#ec4899', // pink
    '#14b8a6', // teal
  ];

  // Renderizar dropdown con portal
  const renderDropdown = () => {
    if (!isOpen) return null;

    return ReactDOM.createPortal(
      <>
      {asSheet && <div className="epic-sheet-overlay" aria-hidden="true" />}
      <div
        ref={dropdownRef}
        className={`epic-dropdown epic-dropdown-portal${asSheet ? ' epic-sheet u-sheet' : ''}`}
        role={asSheet ? 'dialog' : undefined}
        aria-modal={asSheet ? 'true' : undefined}
        aria-label={asSheet ? 'Elegir épica' : undefined}
        tabIndex={asSheet ? -1 : undefined}
        style={{
          position: 'absolute',
          top: `${dropdownPosition.top}px`,
          left: `${dropdownPosition.left}px`,
          width: `${dropdownPosition.width}px`,
          zIndex: 9999
        }}
      >
        {/* Cabecera de la hoja (solo teléfono): título y cierre de 44 px */}
        {asSheet && !isCreating && (
          <div className="epic-sheet-header">
            <span className="epic-sheet-title">Épica</span>
            <button
              type="button"
              className="epic-sheet-close"
              onClick={() => { setIsOpen(false); setSearchTerm(''); }}
              aria-label="Cerrar"
            >
              <X size={18} />
            </button>
          </div>
        )}
        {/* Barra de búsqueda */}
        {!isCreating && (
          <div className="epic-search-container">
            <input
              ref={inputRef}
              type="text"
              className="epic-search-input"
              placeholder="Buscar épica..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus={!asSheet}
            />
          </div>
        )}

        {/* Lista de épicas */}
        {!isCreating && (
          <div className="epic-list">
            {filteredEpics.length > 0 ? (
              filteredEpics.map((epic) => (
                <button
                  key={epic.id}
                  type="button"
                  className={`epic-item ${epic.nombre === value ? 'selected' : ''}`}
                  onClick={() => handleSelect(epic.nombre)}
                >
                  <span
                    className="epic-color-dot"
                    style={{ backgroundColor: epic.color }}
                  />
                  <span className="epic-item-name">{epic.nombre}</span>
                  {epic.totalTareas > 0 && (
                    <span className="epic-task-count">
                      {epic.totalTareas}
                    </span>
                  )}
                  {epic.nombre === value && (
                    <Check size={16} className="epic-check-icon" />
                  )}
                </button>
              ))
            ) : (
              <div className="epic-list-empty">
                {searchTerm ? (
                  <p>No se encontraron épicas que coincidan con "{searchTerm}"</p>
                ) : (
                  <p>No hay épicas disponibles</p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Formulario de creación */}
        {isCreating && (
          <div className="epic-create-form">
            <div className="epic-form-header">
              <h4>Crear nueva épica</h4>
              <button
                type="button"
                className="epic-close-button"
                aria-label="Cerrar"
                onClick={() => {
                  setIsCreating(false);
                  setNewEpicName('');
                  setNewEpicColor('#6366f1');
                }}
                disabled={isLoading}
              >
                <X size={16} />
              </button>
            </div>

            <div className="epic-form-content">
              <div className="epic-form-field">
                <label className="epic-form-label">Nombre de la épica</label>
                <input
                  type="text"
                  className="epic-form-input"
                  placeholder="ej: Performance, SEO, Bug Fix..."
                  value={newEpicName}
                  onChange={(e) => setNewEpicName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleCreateEpic();
                    }
                  }}
                  disabled={isLoading}
                  autoFocus
                />
              </div>

              <div className="epic-form-field">
                <label className="epic-form-label">Color</label>
                <div className="epic-color-picker">
                  {colorOptions.map((color) => (
                    <button
                      key={color}
                      type="button"
                      className={`epic-color-option ${newEpicColor === color ? 'selected' : ''}`}
                      style={{ backgroundColor: color }}
                      onClick={() => setNewEpicColor(color)}
                      disabled={isLoading}
                    >
                      {newEpicColor === color && <Check size={14} />}
                    </button>
                  ))}
                </div>
              </div>

              <div className="epic-form-actions">
                <button
                  type="button"
                  className="epic-form-button secondary"
                  onClick={() => {
                    setIsCreating(false);
                    setNewEpicName('');
                    setNewEpicColor('#6366f1');
                  }}
                  disabled={isLoading}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="epic-form-button primary"
                  onClick={handleCreateEpic}
                  disabled={isLoading || !newEpicName.trim()}
                >
                  {isLoading ? 'Creando...' : 'Crear Épica'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Botón para crear nueva épica */}
        {!isCreating && (
          <button
            type="button"
            className="epic-create-button"
            onClick={() => setIsCreating(true)}
          >
            <Plus size={16} />
            <span>Crear nueva épica</span>
          </button>
        )}
      </div>
      </>,
      document.body // Renderizar en el body
    );
  };

  return (
    <div className="epic-selector-container">
      {/* Botón selector principal */}
      <button
        ref={buttonRef}
        type="button"
        className={`epic-selector-button ${error ? 'error' : ''} ${disabled ? 'disabled' : ''}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
      >
        <div className="epic-selector-content">
          <Target size={16} className="epic-selector-icon" />
          {selectedEpic ? (
            <div className="epic-selected">
              <span
                className="epic-color-dot"
                style={{ backgroundColor: selectedEpic.color }}
              />
              <span className="epic-name">{selectedEpic.nombre}</span>
            </div>
          ) : (
            <span className="epic-placeholder">{placeholder}</span>
          )}
        </div>
        <ChevronDown
          size={16}
          className={`epic-chevron ${isOpen ? 'open' : ''}`}
        />
      </button>

      {error && <span className="epic-selector-error">{error}</span>}

      {/* Renderizar dropdown con portal */}
      {renderDropdown()}
    </div>
  );
}

export default EpicSelector;
